import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard, Bot, BookOpen, KeyRound, ShieldAlert, ScrollText, LogOut, Activity, Menu, X, Swords
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/bots", label: "Bot Management", icon: Bot },
  { path: "/manual-trade", label: "Manual Trading", icon: Swords },
  { path: "/orderbook", label: "Orderbook", icon: BookOpen },
  { path: "/api-keys", label: "API Keys", icon: KeyRound },
  { path: "/risk", label: "Risk Controls", icon: ShieldAlert },
  { path: "/logs", label: "Activity Logs", icon: ScrollText },
];

export default function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      {/* Mobile Header - Only visible on small screens */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border/50 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-primary/20 flex items-center justify-center">
            <Activity className="w-4 h-4 text-primary" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-sm font-bold tracking-tight text-foreground">VAULT</h1>
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Market Ops</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleMobileMenu}
          className="h-9 w-9 p-0"
        >
          {isMobileMenuOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <Menu className="w-5 h-5" />
          )}
        </Button>
      </header>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 top-16"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar - Desktop (always visible) & Mobile (slide in) */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 bg-card border-r border-border/50 flex flex-col z-50
          transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:top-0
          top-16
        `}
        data-testid="sidebar"
      >
        {/* Logo - Desktop only */}
        <div className="hidden lg:block p-5 border-b border-border/40">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-primary/20 flex items-center justify-center">
              <Activity className="w-4 h-4 text-primary" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-tight text-foreground">VAULT</h1>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Market Ops</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={`sidebar-nav-item ${active ? "active" : "text-muted-foreground"}`}
                onClick={closeMobileMenu}
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <Separator />

        {/* User */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-primary">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground truncate">{user?.name}</p>
              <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground text-xs"
            onClick={() => {
              logout();
              closeMobileMenu();
            }}
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>
    </>
  );
}
